import { DeleteFilled } from "@ant-design/icons";
import { Button, Col, Divider, Form, FormInstance, Input, Radio, Row, Select } from "antd";
import { Rule, RulePin, RuleUIAction, RuleUICondition, RuleUIModel } from "models/rules";
import { forwardRef, Fragment, PropsWithChildren, Ref, useCallback, useImperativeHandle, useState } from "react";
import { ActionMap } from "./Actions";
import { ConditionMap } from "./Conditions";

export interface NewRuleFormRef {
    reset: () => void;
}

interface NewRuleFormProps {
    form: FormInstance<Rule>;
    onChange: (conditions: RuleUICondition[], action: RuleUIAction) => void;
}

export const BlankSlateRule: RuleUIModel = {
    conditions: [],
    action: RuleUIAction.Unknown
};

const StyledDivider = ({ children }: PropsWithChildren) =>
    <Divider orientation="left" style={{ margin: '20px 0', lineHeight: '0px' }}>{children}</Divider>

const NewRuleForm = ({ onChange, form }: NewRuleFormProps, ref?: Ref<NewRuleFormRef>) => {
    const [rule, setRule] = useState<RuleUIModel>(() => ({ ...BlankSlateRule }));

    useImperativeHandle(ref, () => ({
        reset: resetForm,
    }));

    const addCondition = (id: number) => {
        setRule(cur => ({ ...cur, conditions: [...cur.conditions, id] }));
        onChange([...rule.conditions, id], rule.action);
    }

    const setAction = (id: number) => {
        form.setFieldValue('action', undefined);
        setRule(cur => ({ ...cur, action: id }));
        onChange([...rule.conditions], id);
    }

    const removeCondition = useCallback((idx: number) => {
        rule.conditions.splice(idx, 1);

        setRule(cur => ({
            conditions: [...rule.conditions],
            action: rule.conditions.length === 0 ? RuleUIAction.Unknown : cur.action
        }));
        onChange([...rule.conditions], rule.action);
    }, [rule, onChange]);

    const resetForm = () => {
        setRule(cur => ({ ...cur, ...BlankSlateRule }));
        form.resetFields();
        onChange(BlankSlateRule.conditions, BlankSlateRule.action);
    }

    const hasConditions = rule.conditions.length > 0;
    const conflictingCondition = rule.conditions.some(c => c === RuleUICondition.Always);

    const conditionNodeList = useCallback((
        _: any,
        operation: {
            remove: (index: number | number[]) => void
        }
    ) => rule.conditions.map((c, idx) => {
        const ConditionNode = ConditionMap[c];
        const key = `condition-${crypto.randomUUID()}`;
        const deleteButton = <Button danger
            icon={<DeleteFilled />}
            onClick={() => {
                removeCondition(idx);
                operation.remove(idx);
            }}
            style={{ marginRight: '3px' }}
        />;

        if (idx + 1 === rule.conditions.length) {
            return <Fragment key={key}>
                {deleteButton}
                <ConditionNode index={idx} />
            </Fragment>
        }

        return <Fragment key={key}>
            {deleteButton}
            <ConditionNode index={idx} />
            <StyledDivider>And</StyledDivider>
        </Fragment>
    }), [rule, removeCondition]);

    const ActionNode = ActionMap[rule.action];

    return <>
        <Row gutter={[4, 16]} style={{ marginBottom: '32px' }}>
            <Col span={3}>
                <Button danger type="dashed" onClick={resetForm}>Reset</Button>
            </Col>

            <Col span={13}>
                <Select
                    options={[
                        { label: "- Select a condition -", value: RuleUICondition.Unknown },
                        { label: "Always", value: RuleUICondition.Always, disabled: hasConditions },
                        { label: "When the title contains...", value: RuleUICondition.Contains, disabled: conflictingCondition },
                        { label: "When the title does not contain...", value: RuleUICondition.DoesNotContain, disabled: conflictingCondition },
                        { label: "When the title length is less than or equal to...", value: RuleUICondition.TitleLengthLtE, disabled: conflictingCondition },
                        { label: "When the title length is greater than or equal to...", value: RuleUICondition.TitleLengthGtE, disabled: conflictingCondition },
                        { label: "When the title length is exactly...", value: RuleUICondition.TitleLengthExact, disabled: conflictingCondition },
                    ]}
                    onChange={addCondition}
                    style={{ width: '100%' }}
                    value={0} />
            </Col>

            <Col span={8}>
                <Select
                    options={[
                        { label: "- Select an action -", value: RuleUIAction.Unknown },
                        { label: "Insert At...", value: RuleUIAction.InsertAt, disabled: !hasConditions },
                        { label: "Remove...", value: RuleUIAction.Remove, disabled: !hasConditions },
                        { label: "Replace...", value: RuleUIAction.Replace, disabled: !hasConditions },
                        { label: "Drop Characters...", value: RuleUIAction.DropChars, disabled: !hasConditions },
                    ]}
                    onChange={setAction}
                    style={{ width: '100%' }}
                    value={0} />
            </Col>
        </Row>
        <Row gutter={[32, 32]}>
            <Col span={24}>
                <Form layout="inline" form={form} initialValues={{ conditions: undefined, action: undefined, pin: RulePin.None }}>
                    <Form.List name='conditions'>
                        {conditionNodeList}
                    </Form.List>
                    {rule.action !== RuleUIAction.Unknown && <>
                        {!conflictingCondition && <StyledDivider>Then</StyledDivider>}
                        <ActionNode />
                    </>}
                    <Form.Item name="ordinal" initialValue={0} hidden>
                        <Input />
                    </Form.Item>
                    {rule.action !== RuleUIAction.Unknown && <>
                        <Divider style={{ alignSelf: 'baseline' }}>Rule Options</Divider>
                        <Form.Item name="pin" label="Pin Rule To:">
                            <Radio.Group optionType="button" buttonStyle="solid">
                                <Radio value={RulePin.None}>Don't Pin</Radio>
                                <Radio value={RulePin.Top}>Top</Radio>
                                <Radio value={RulePin.Bottom}>Bottom</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </>}
                </Form>
            </Col>
        </Row>
    </>
};

export default forwardRef<NewRuleFormRef, NewRuleFormProps>(NewRuleForm);